import type { Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XGrid } from '@adornis/chemistry/elements/components/x-grid.js';
import { html, nothing, type PropertyValueMap, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { debounceTime, distinctUntilChanged, firstValueFrom, timer } from 'rxjs';
import '../theme/d-icon.js';
import './component-swipe.js';

export enum ElementAttribute {
  IS_ANIMATING = 'is-animating',
}

export enum ElementCSSClass {
  CAROUSEL_ITEM = 'carousel-item',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum ElementCSSAnimation {
  ANIMATE_RIGHT = 'animate-right',
  ANIMATE_LEFT = 'animate-left',
}

/**
 * Carousel Frontend Component
 * @property {number} itemWidth width as number implicit in px
 * @property {number} itemGap gap as number implicit in px
 */
@customElement('component-carousel')
export class ComponentCarousel extends ChemistryLitElement {
  @property({ attribute: 'visible-items', type: Number }) visibleItems: number = 2;
  @property({ attribute: 'item-gap', type: Number }) itemGap = 8;
  @property({ attribute: 'min-height', type: Number }) minHeight = 0;
  @property({ attribute: 'carousel-title', type: String }) carouselTitle = 'Titel';
  @property({ attribute: 'overflow', type: String }) overflow: 'hidden' | 'visible' = 'hidden';
  @property({ attribute: 'touch', type: Boolean }) isTouchControled = false;
  @property({ attribute: 'infinity', type: Boolean }) isInfinity = false;
  @property({ attribute: 'duration', type: Number }) duration = 500;
  @property({ attribute: false }) items: Array<(isActive: boolean) => TemplateResult> = [];

  @state() private readonly _componentSize = new ComponentSizeController(this);
  @state() private readonly _windowSize = new WindowSizeController(this);
  @state() private readonly _carousel = createRef<XGrid>();
  @state() private offsetItems = 0;

  override connectedCallback(): void {
    super.connectedCallback();

    this._componentSize.observable
      .pipe(distinctUntilChanged(), debounceTime(300))
      .subscribe(() => (this._heightOfItem = 0));
  }

  @state() private _heightOfItem?: number;
  override updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    super.updated(_changedProperties);

    const tempContainer = this.renderRoot.querySelector('#temp');
    if (!tempContainer) return;
    const children = tempContainer.children;
    const maxHeight = Array.from(children).reduce((maxHeight, element) => {
      const height = element.getBoundingClientRect().height;
      if (maxHeight > height) return maxHeight;
      else return height;
    }, 0);
    if (this._heightOfItem === maxHeight) return;
    this._heightOfItem = maxHeight;
  }

  protected override willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.willUpdate(_changedProperties);

    if (!this.shouldBeInfinity() && (this.offsetItems < 0 || this.offsetItems > this.items.length - 1)) {
      this.offsetItems = this.parseToValidIndex(this.offsetItems);
    }
  }

  override render() {
    const COUNT_ITEMS = this.items.length;
    const PADDING_INNER_WRAPPER = '10px';

    return html`
      <d-grid
        id="temp"
        ${css({
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          transform: `translateX(${this.offset()}px)`,
          gridTemplateColumns: `repeat(${COUNT_ITEMS}, ${this.itemWidth()}px)`,
          gap: `${this.itemGap}px`,
          transition: `${this.duration}ms`,
          ...(this.minHeight ? { minHeight: `${this.minHeight}px` } : {}),
        })}
      >
        ${repeat(this.items, item => item(true))}
      </d-grid>

      <component-swipe
        @swipe-right=${() => {
          this._controlRightAction();
        }}
        @swipe-left=${() => {
          this._controlLeftAction();
        }}
      >
        <d-flex
          space="lg"
          ${css({
            overflow: this.overflow,
            padding: PADDING_INNER_WRAPPER,
            transform: `translateX(-${PADDING_INNER_WRAPPER})`,
            width: `calc(100% + 2 * ${PADDING_INNER_WRAPPER})`,
          })}
        >
          <!-- left -->
          ${this._controlLeft()}

          <!-- right -->
          ${this._controlRight()}
          <d-grid
            ${ref(this._carousel)}
            ${css({
              transform: `translateX(${this.offset()}px)`,
              gridTemplateColumns: `repeat(${
                this.shouldBeInfinity() ? this.visibleItems * (3 + 2) : COUNT_ITEMS
              }, ${this.itemWidth()}px)`,
              gap: `${this.itemGap}px`,
              transition: `${this.duration}ms`,
              ...(this.minHeight ? { minHeight: `${this.minHeight}px` } : {}),
            })}
          >
            ${this._carouselItems()}
          </d-grid>
        </d-flex>
      </component-swipe>
    `;
  }

  protected _carouselItems() {
    return html`
      <!-- left inactive -->
      ${this._carouselInactiveItemsLeft()}

      <!-- active -->
      ${this._carouselActiveItems()}

      <!-- right inactive -->
      ${this._carouselInactiveItemsRight()}
    `;
  }

  protected _carouselActiveItems() {
    const fromIndex = this.offsetItems;
    const tillIndexMax = this.items.length - 1;
    const tillIndexVisible = fromIndex + this.visibleItems - 1;
    const tillIndex = this.shouldBeInfinity()
      ? tillIndexVisible
      : tillIndexVisible > tillIndexMax
      ? tillIndexMax
      : tillIndexVisible;

    let elements: TemplateResult[] = [];
    for (let i = fromIndex; i <= tillIndex; i++) {
      const itemFunction = this.getItemFunctionByIndex(i);
      if (!itemFunction) continue;
      elements.push(
        html` <div class=${`${ElementCSSClass.ACTIVE} ${ElementCSSClass.CAROUSEL_ITEM}`}>${itemFunction(true)}</div> `,
      );
    }
    return html` ${elements} `;
  }

  protected _carouselInactiveItemsLeft() {
    const fromIndex = this.shouldBeInfinity() ? this.offsetItems - this.visibleItems * 2 : 0;
    const countItems = this.visibleItems * 2;
    const tillIndex = this.shouldBeInfinity() ? fromIndex - 1 + countItems : this.offsetItems - 1;

    let elements: TemplateResult[] = [];
    for (let i = fromIndex; i <= tillIndex; i++) {
      const itemFunction = this.getItemFunctionByIndex(i);
      if (!itemFunction) continue;
      elements.push(
        html`
          <div class=${`${ElementCSSClass.LEFT} ${ElementCSSClass.INACTIVE} ${ElementCSSClass.CAROUSEL_ITEM}`}>
            ${itemFunction(false)}
          </div>
        `,
      );
    }

    return html` ${elements} `;
  }

  protected _carouselInactiveItemsRight() {
    const fromIndex = this.offsetItems + this.visibleItems;
    const countItems = this.visibleItems * 2;
    const tillIndex = this.shouldBeInfinity() ? fromIndex - 1 + countItems : this.items.length - 1;

    let elements: TemplateResult[] = [];
    for (let i = fromIndex; i <= tillIndex; i++) {
      const itemFunction = this.getItemFunctionByIndex(i);
      if (!itemFunction) continue;
      elements.push(
        html`
          <div class=${`${ElementCSSClass.CAROUSEL_ITEM} ${ElementCSSClass.RIGHT} ${ElementCSSClass.INACTIVE} `}>
            ${itemFunction(false)}
          </div>
        `,
      );
    }
    return html` ${elements} `;
  }

  protected parseToValidIndex(index: number): number {
    const clockwise = index >= 0;
    // + index weil der index bereits negativ ist.
    // -> Sonst würden wir diesen addieren und nicht subtrahieren
    const targetIndex = clockwise ? index : this.items.length + index;
    const validIndex = targetIndex % this.items.length;
    if (Number.isNaN(validIndex)) return 0;
    return validIndex;
  }

  protected getItemFunctionByIndex(index: number) {
    return this.items.at(this.parseToValidIndex(index));
  }

  protected _controlLeft() {
    if ((this.offsetItems <= 0 || this.isTouchControled) && !this.shouldBeInfinity()) return nothing;

    return html`
      <d-icon
        control
        solid
        ${css({
          left: '-60px',
        })}
        @click=${() => {
          this._controlLeftAction();
        }}
      >
        chevron-left
      </d-icon>
    `;
  }

  protected _controlRight() {
    const ITEMS_RIGHT_OVERFLOW = this.itemsOverflowRight();
    if ((ITEMS_RIGHT_OVERFLOW <= 0 || this.isTouchControled) && !this.shouldBeInfinity()) return nothing;

    return html`
      <d-icon
        control
        solid
        ${css({
          right: '-60px',
        })}
        @click=${() => {
          this._controlRightAction();
        }}
      >
        chevron-right
      </d-icon>
    `;
  }

  protected _control(direction: 'left' | 'right') {
    const iconStyling = () => {
      if (direction === 'left') return { left: '-60px' };
      if (direction === 'right') return { right: '-60px' };
      throw new Error('invalid direction');
    };

    const iconName = () => {
      if (direction === 'left') return 'chevron-left';
      if (direction === 'right') return 'chevron-right';
      throw new Error('invalid direction');
    };

    return html`
      <d-icon
        control
        solid
        ${css(iconStyling())}
        @click=${() => {
          // this._controlRightAction();
        }}
      >
        ${iconName()}
      </d-icon>
    `;
  }

  protected async _controlLeftAction() {
    if (this.hasAttribute(ElementAttribute.IS_ANIMATING)) return;

    switch (this.shouldBeInfinity()) {
      case true:
        this.setAttribute(ElementAttribute.IS_ANIMATING, 'true');
        const carouselContainer = this._carousel.value;
        if (!carouselContainer) break;

        const activeElements = Array.from(this.renderRoot.querySelectorAll('.active').values());
        const firstActiveElement = activeElements[0];
        const sibling = firstActiveElement?.previousElementSibling;
        sibling?.classList.remove('inactive');
        sibling?.classList.add('active');

        carouselContainer.classList.add(ElementCSSAnimation.ANIMATE_LEFT);

        // wait till the animation is done and continue
        await firstValueFrom(timer(this.duration));

        sibling?.classList.add('inactive');
        sibling?.classList.remove('active');

        carouselContainer.classList.remove(ElementCSSAnimation.ANIMATE_LEFT);
        break;
      case false:
        if (!this._carousel.value) return;
        if (this.offsetItems === 0) return;
        this.setAttribute(ElementAttribute.IS_ANIMATING, 'true');
        break;
    }

    this.offsetItems--;
    this.removeAttribute(ElementAttribute.IS_ANIMATING);
  }

  protected async _controlRightAction() {
    if (this.hasAttribute(ElementAttribute.IS_ANIMATING)) return;

    switch (this.shouldBeInfinity()) {
      case true:
        this.setAttribute(ElementAttribute.IS_ANIMATING, 'true');
        const carouselContainer = this._carousel.value;
        if (!carouselContainer) break;

        const activeElements = Array.from(this.renderRoot.querySelectorAll('.active').values());
        const lastActiveElement = activeElements[activeElements.length - 1];
        const sibling = lastActiveElement?.nextElementSibling;
        sibling?.classList.remove('inactive');
        sibling?.classList.add('active');

        carouselContainer.classList.add(ElementCSSAnimation.ANIMATE_RIGHT);

        // wait till the animation is done and continue
        await firstValueFrom(timer(this.duration));

        sibling?.classList.add('inactive');
        sibling?.classList.remove('active');

        carouselContainer.classList.remove(ElementCSSAnimation.ANIMATE_RIGHT);
        break;
      case false:
        if (!this._carousel.value) return;
        if (this.itemsOverflowRight() <= 0) return;
        this.setAttribute(ElementAttribute.IS_ANIMATING, 'true');
        break;
    }

    this.offsetItems++;
    this.removeAttribute(ElementAttribute.IS_ANIMATING);
  }

  protected containerWidth() {
    const componentWidth = this._componentSize.value?.width ?? 0;
    return componentWidth;
  }

  protected itemWidth() {
    const gapWidth = (this.visibleItems - 1) * this.itemGap;
    const itemsWidth = this.containerWidth() - gapWidth;
    const itemWidth = itemsWidth / this.visibleItems;
    return itemWidth;
  }

  protected offset(addItems: number = 0) {
    const offsetItems = this.shouldBeInfinity() ? this.visibleItems * 2 : this.offsetItems;
    return (offsetItems + addItems) * (this.itemWidth() + this.itemGap) * -1;
  }

  protected itemsOverflowRight() {
    return this.items.length - this.offsetItems - this.visibleItems;
  }

  protected modifyClasslist(node: Element, ...classes: ElementCSSClass[]) {
    node.classList.remove(...Object.values(ElementCSSClass));
    node.classList.add(ElementCSSClass.CAROUSEL_ITEM);

    for (const className of classes) {
      // default behaviour
      node.classList.add(className);
    }
  }

  protected shouldBeInfinity() {
    return (this.isInfinity || (this._windowSize.value?.width ?? 0) <= 1630) && this.hasOverflowingItems();
  }

  protected hasOverflowingItems() {
    return this.items.length > this.visibleItems;
  }

  override styles() {
    // default stylings

    // add margin left, right for controls on too small screnn
    const windowSize = this._windowSize.value?.width ?? 0;
    const addControlsMargin = windowSize <= 1630 && this.hasOverflowingItems();

    const styling = [
      ...super.styles(),
      {
        ':host': {
          display: 'block',
          margin: addControlsMargin ? '0 80px 0 0 !important' : '',
        },
        '[control]': {
          fontSize: '20px',
          cursor: 'pointer',
          color: this.colors.tone.black,
          position: 'absolute',
          padding: '8px',
          borderRadius: '50%',
          top: '50%',
          transform: 'translateY(-50%)',
          background: '#fff',
          boxShadow: '4px 4px 40px rgba(0, 0, 0, 0.1)',
          zIndex: '100',
          userSelect: 'none',
        },
        '[control]:hover': {
          transition: '200ms',
          scale: '1.05',
        },
      },
    ] as Styles[];

    // add styling for items out of container range
    const componentSize = this._componentSize.value?.width ?? 0;
    const visibleXLeft = this.offset() * -1;
    const visibleXRight = visibleXLeft + componentSize;

    for (const [index, child] of this.items.entries()) {
      if (!child) continue;
      const xLeft = index * (this.itemGap + this.itemWidth());
      const xRight = xLeft + this.itemWidth();

      if (xLeft < visibleXLeft || xRight > visibleXRight) {
        styling.push({
          [`.${ElementCSSClass.INACTIVE}`]: {
            filter: 'opacity(25%)',
          },
        });
      } else {
        styling.push({
          [`.active`]: {},
        });
      }
    }

    // default styling for slotted items
    styling.push({
      [`.${ElementCSSClass.CAROUSEL_ITEM}`]: {
        boxSizing: 'border-box',
        width: `${this.itemWidth()}px !important`,
        boxShadow: '4px 4px 40px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        userSelect: 'none',
        background: '#fff',
        height: this._heightOfItem ? `${this._heightOfItem}px` : '',
      },
    });

    styling.push({
      [`.${ElementCSSClass.CAROUSEL_ITEM}:hover`]: {
        boxShadow: '4px 4px 50px rgba(0, 0, 0, 0.15)',
      },
    });

    styling.push({
      [`.${ElementCSSClass.CAROUSEL_ITEM}.${ElementCSSClass.INACTIVE}:hover`]: {
        // filter: 'opacity(50%)',
      },
    });

    // animation right for inifinity scroll
    styling.push({
      [`@keyframes ${ElementCSSAnimation.ANIMATE_RIGHT}`]: {
        '1%': {
          transform: `translateX(${this.offset()}px)`,
          transition: `${this.duration}ms`,
        },
        '100%': {
          transform: `translateX(${this.offset(1)}px)`,
        },
      },
    });

    styling.push({
      [`.${ElementCSSAnimation.ANIMATE_RIGHT}`]: {
        animation: `${ElementCSSAnimation.ANIMATE_RIGHT} ${this.duration}ms forwards`,
      },
    });

    // animation left for inifinity scroll
    styling.push({
      [`@keyframes ${ElementCSSAnimation.ANIMATE_LEFT}`]: {
        '1%': {
          transform: `translateX(${this.offset()}px)`,
          transition: `${this.duration}ms`,
        },
        '100%': {
          transform: `translateX(${this.offset(-1)}px)`,
        },
      },
    });

    styling.push({
      [`.${ElementCSSAnimation.ANIMATE_LEFT}`]: {
        animation: `${ElementCSSAnimation.ANIMATE_LEFT} ${this.duration}ms forwards`,
      },
    });

    return styling;
  }
}
